import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/feedback";
import { LoadingTable } from "@/components/ui/system-state";
import { SpecimenCard, SpecimenSection } from "@/features/system-states/system-state-page";
import { cn } from "@/lib/utils";

function Spinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block size-4 shrink-0 animate-spin rounded-full border-2 border-primary/25 border-t-primary",
        className
      )}
    />
  );
}

function SkeletonCard() {
  return (
    <SpecimenCard label="Card skeleton" className="max-w-[360px]">
      <div className="mb-4 flex items-center gap-3">
        <Skeleton shimmer className="size-9 shrink-0 rounded-full" />
        <div className="flex-1">
          <Skeleton shimmer className="mb-2 h-3.5 w-3/4" />
          <Skeleton shimmer className="h-3.5 w-1/2" />
        </div>
      </div>
      <Skeleton shimmer className="mb-2 h-3.5 w-full" />
      <Skeleton shimmer className="mb-2 h-3.5 w-3/4" />
      <Skeleton shimmer className="h-3.5 w-1/2" />
      <div className="mt-3 flex gap-3">
        <Skeleton shimmer className="h-[52px] flex-1 rounded" />
        <Skeleton shimmer className="h-[52px] flex-1 rounded" />
      </div>
    </SpecimenCard>
  );
}

function StatGridSkeleton() {
  return (
    <SpecimenCard label="Stat card grid skeleton (4 cards)">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded border border-line p-4">
            <Skeleton shimmer className="mb-2 h-7 rounded" />
            <Skeleton shimmer className="h-3 w-3/5 rounded" />
          </div>
        ))}
      </div>
    </SpecimenCard>
  );
}

function TopbarSkeleton() {
  return (
    <SpecimenCard label="Topbar — loading" className="p-0">
      <div className="flex h-14 items-center gap-3 border-b border-line px-6">
        <Skeleton shimmer className="size-9 rounded-full" />
        <div className="ml-auto flex items-center gap-2">
          <Skeleton shimmer className="size-7 rounded-full" />
          <Skeleton shimmer className="size-7 rounded-full" />
          <Skeleton shimmer className="size-9 rounded-full" />
        </div>
      </div>
    </SpecimenCard>
  );
}

export default function SysLoading() {
  const [overlay, setOverlay] = useState(false);

  function simulateGlobalLoad() {
    setOverlay(true);
    setTimeout(() => setOverlay(false), 1800);
  }

  return (
    <>
      {overlay && (
        <div
          role="status"
          aria-label="Loading transfers"
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-4 bg-console-bg/80 backdrop-blur-[2px]"
        >
          <span
            aria-hidden
            className="size-11 animate-spin rounded-full border-[3px] border-white/20 border-t-white"
          />
          <div className="text-base font-semibold text-white">Loading transfers…</div>
          <div className="-mt-2 text-sm text-white/60">Fetching from VaultCore and Fireblocks</div>
        </div>
      )}

      <SpecimenSection label="Global / Full-page loading">
        <SpecimenCard label="Full-page blocking overlay" className="max-w-[520px]">
          <p className="mb-4 text-sm text-muted">
            Used when navigating between pages or when the entire view must be blocked while data loads. Dark
            semi-transparent overlay with a centered spinner.
          </p>
          <Button loading={overlay} onClick={simulateGlobalLoad}>
            Simulate Full-Page Load
          </Button>
        </SpecimenCard>
      </SpecimenSection>

      <SpecimenSection label="Skeleton / shimmer loading">
        <div className="flex flex-col gap-3 lg:flex-row">
          <SkeletonCard />
          <div className="flex-1">
            <div className="mb-3 text-xs font-semibold text-muted">Table skeleton (5 rows)</div>
            <LoadingTable rows={5} cols={5} />
          </div>
        </div>
        <StatGridSkeleton />
      </SpecimenSection>

      <SpecimenSection label="Component loading states">
        <div className="grid gap-3 lg:grid-cols-3">
          <SpecimenCard label="Button — loading state">
            <div className="flex flex-wrap gap-3">
              <Button loading>Processing…</Button>
              <Button variant="secondary" loading>
                Submitting…
              </Button>
            </div>
          </SpecimenCard>

          <SpecimenCard label="Badge — verifying state">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="info" dot={false}>
                <Spinner className="size-2.5 border-info/30 border-t-info" /> Verifying…
              </Badge>
              <Badge variant="pending" dot={false}>
                <Spinner className="size-2.5 border-pending/30 border-t-pending" /> Screening
              </Badge>
            </div>
          </SpecimenCard>

          <SpecimenCard label="Inline spinner (data refresh)">
            <div className="flex items-center gap-2 text-sm text-muted">
              <Spinner /> Refreshing balance from Fireblocks…
            </div>
          </SpecimenCard>
        </div>
        <TopbarSkeleton />
      </SpecimenSection>
    </>
  );
}
